import React, { Component } from "react";

export default class DefaultSettings extends Component {
  state = {
    openPanels: {
      operationalPeriod: true,
      constructionCapital: false,
      directLabor: false,
      indirectLabor: false,
      otherCosts: false,
    },
    animating: null
  };

  // Panel order for auto-advance
  panelOrder = [
    "operationalPeriod",
    "constructionCapital",
    "directLabor",
    "indirectLabor",
    "otherCosts"
  ];

  componentDidMount() {
    document.addEventListener("keydown", this.handleEscClose);
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.handleEscClose);
  }

  handleEscClose = (event) => {
    if (event.key === "Escape") this.props.onClose();
  };

  handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) this.props.onClose();
  };

  togglePanel = (panel) => {
    this.setState((prev) => {
      const isOpen = prev.openPanels[panel];
      return {
        openPanels: {
          operationalPeriod: false,
          constructionCapital: false,
          directLabor: false,
          indirectLabor: false,
          otherCosts: false,
          [panel]: !isOpen,
        },
        animating: panel
      };
    });
  };

  // Auto open next section or close modal (Option B)
  handleSave = () => {
    const currentOpen = Object.keys(this.state.openPanels).find(
      (key) => this.state.openPanels[key]
    );

    const currentIndex = this.panelOrder.indexOf(currentOpen);
    const isLast = currentIndex === this.panelOrder.length - 1;

    if (isLast) {
      this.props.onSave(); // Close modal
      return;
    }

    const nextPanel = this.panelOrder[currentIndex + 1];
    this.setState({
      openPanels: {
        operationalPeriod: false,
        constructionCapital: false,
        directLabor: false,
        indirectLabor: false,
        otherCosts: false,
        [nextPanel]: true,
      },
      animating: nextPanel
    });

    this.props.onSave(); // Save but modal stays open
  };

  renderInput(section, key, label, value) {
    return (
      <>
        <label className="text-sm font-medium">{label}</label>
        <input
          type="number"
          value={value ?? ""}
          onChange={(e) => this.props.onChange(section, key, e.target.value)}
          className="border p-2 rounded"
        />
      </>
    );
  }

  render() {
    const { settings, onClose, onReset } = this.props;
    const { openPanels } = this.state;
    console.log(settings)
    const {
      operatingYears,
      operatingHoursPerYear,
      constructionCapital = {},
      directLabor = {},
      indirectLabor = {},
      otherCosts = {},
    } = settings;

    const Chevron = ({ open }) => (
      <svg
        className={`w-5 h-5 transform transition-transform duration-300 ${
          open ? "rotate-180" : "rotate-0"
        }`}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round"
          d="M4.5 8.25l7.5 7.5 7.5-7.5" />
      </svg>
    );

    const Panel = ({ name, label, children }) => (
      <div className="border rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => this.togglePanel(name)}
          className="w-full flex justify-between items-center px-4 py-2 bg-gray-50 hover:bg-gray-100"
        >
          <span className="font-medium">{label}</span>
          <Chevron open={openPanels[name]} />
        </button>

        <div
          className={`transition-all duration-300 ease-in-out overflow-hidden ${
            openPanels[name] ? "max-h-[500px] opacity-100 p-2" : "max-h-0 opacity-0 p-0"
          }`}
        >
          {openPanels[name] && (
            <div className="grid grid-cols-2 gap-2">
              {children}
            </div>
          )}
        </div>
      </div>
    );

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
        onClick={this.handleOverlayClick}
      >
        <div style={{"min-width": "520px", "maxWidth": "520px"}} className="bg-white rounded-2xl max-h-[75vh] shadow-lg border flex flex-col overflow-hidden">

          {/* Sticky Header */}
          <div className="sticky top-0 bg-white p-4 border-b z-10">
            <h2 className="text-lg font-semibold">Main Tool Settings</h2>
          </div>

          {/* Scrollable Panels */}
          <div className="overflow-y-auto p-2 space-y-4">

            <Panel name="operationalPeriod" label="Operational Period">
              {this.renderInput(null, "operatingYears", "Operating years", operatingYears)}
              {this.renderInput(null, "operatingHoursPerYear", "Hours per year", operatingHoursPerYear)}
            </Panel>

            <Panel name="constructionCapital" label="Construction & Capital">
              {this.renderInput("constructionCapital", "startProjectYear", "Start Project Year", constructionCapital.startProjectYear)}
              {this.renderInput("constructionCapital", "firstOperationYear", "First Operation Year", constructionCapital.firstOperationYear)}
              {this.renderInput("constructionCapital", "constructionPeriodYears", "Construction Period (yrs)", constructionCapital.constructionPeriodYears)}
              {this.renderInput("constructionCapital", "plantLifeYears", "Plant Life (yrs)", constructionCapital.plantLifeYears)}
              {this.renderInput("constructionCapital", "capitalRecoveryFactorPct", "Capital Recovery", constructionCapital.capitalRecoveryFactorPct)}
              {this.renderInput("constructionCapital", "generalFacilitiesPct", "General Facilities", constructionCapital.generalFacilitiesPct)}
              {this.renderInput("constructionCapital", "homeOfficePct", "Home Office", constructionCapital.homeOfficePct)}
              {this.renderInput("constructionCapital", "projectContingenciesPct", "Project Contingencies", constructionCapital.projectContingenciesPct)}
              {this.renderInput("constructionCapital", "prepaidRoyaltiesPct", "Prepaid Royalties", constructionCapital.prepaidRoyaltiesPct)}
              {this.renderInput("constructionCapital", "startupCostsPct", "Startup Costs", constructionCapital.startupCostsPct)}
              {this.renderInput("constructionCapital", "workingCapitalPct", "Working Capital", constructionCapital.workingCapitalPct)}
            </Panel>

            <Panel name="directLabor" label="Direct Labour">
              {this.renderInput("directLabor", "operatorsPerShift", "Operators / shift", directLabor.operatorsPerShift)}
              {this.renderInput("directLabor", "shiftsPerDay", "Shifts / day", directLabor.shiftsPerDay)}
              {this.renderInput("directLabor", "additionalShifts", "Extra shifts", directLabor.additionalShifts)}
              {this.renderInput("directLabor", "labourCostPerOperator", "Labour Cost (€)", directLabor.labourCostPerOperator)}
              {this.renderInput("directLabor", "directSupervisionPct", "Supervision", directLabor.directSupervisionPct)}
              {this.renderInput("directLabor", "maintenanceLabourPct", "Maintenancez", directLabor.maintenanceLabourPct)}
            </Panel>

            <Panel name="indirectLabor" label="Indirect Labour">
              {this.renderInput("indirectLabor", "indirectLabourPct", "Indirect", indirectLabor.indirectLabourPct)}
            </Panel>

            <Panel name="otherCosts" label="Other Operational Costs">
              {this.renderInput("otherCosts", "maintenanceMaterialPct", "Maintenance Material", otherCosts.maintenanceMaterialPct)}
              {this.renderInput("otherCosts", "indirectMaterialPct", "Indirect Material", otherCosts.indirectMaterialPct)}
              {this.renderInput("otherCosts", "propertyTaxesPct", "Property Tax", otherCosts.propertyTaxesPct)}
              {this.renderInput("otherCosts", "salesExpensePct", "Sales Expense", otherCosts.salesExpensePct)}
            </Panel>

          </div>

          {/* Sticky Footer Buttons */}
          <div className="sticky bottom-0 bg-white p-4 border-t flex justify-end gap-4 z-10">
            <button onClick={onReset} className="px-4 py-2 rounded border">Reset</button>
            <button onClick={onClose} className="px-4 py-2 rounded border">Close</button>
            <button onClick={this.handleSave} className="px-4 py-2 rounded bg-green-600 text-white">Save</button>
          </div>

        </div>
      </div>
    );
  }
}
